import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { Alert, Autocomplete, Box, Button, IconButton, InputAdornment, TextField } from '@mui/material';
import type { GridRowOrderChangeParams } from '@mui/x-data-grid-premium';
import { SideDrawer, Typography } from '@peritasai/ui-components';
import type { AssetCatalogItemEntity, CountSheetEntity } from '@peritasai/repository';
import { UntrackedAssetType } from '@peritasai/types';
import {
  useAssetCatalog,
  useCountSheets,
  useCreateCountSheet,
  useReplaceCountSheet,
  useUntrackedAssetCatalog,
} from '@peritasai/repository';
import { useEffect, useState } from 'react';
import { useDebounce } from '../../../../shared';
import { useTranslation } from 'react-i18next';
import { DrawerMode } from '../../utils/drawer';
import { InstrumentListGrid, type InstrumentFormItem } from './InstrumentListGrid';
import { InstrumentThumbnail } from './InstrumentThumbnail';
import { InstrumentDetailsDialog, type InstrumentDetails } from './InstrumentDetailsDialog';

interface FormErrors {
  trayName?: string;
  instruments?: string;
  apiError?: string;
}

function validate(
  trayName: string,
  trayId: string,
  mode: DrawerMode,
  instruments: InstrumentFormItem[],
  t: (key: string, opts?: Record<string, string>) => string
): FormErrors {
  const errors: FormErrors = {};
  if (mode === DrawerMode.Create && !trayId) {
    errors.trayName = t('pages.entityManagement.drawer.fieldRequired', {
      fieldName: t('pages.entityManagement.countSheetDrawer.trayNameLabel'),
    });
  }
  if (mode === DrawerMode.Edit && !trayName.trim()) {
    errors.trayName = t('pages.entityManagement.drawer.fieldRequired', {
      fieldName: t('pages.entityManagement.countSheetDrawer.trayNameLabel'),
    });
  }
  if (instruments.length === 0) {
    errors.instruments = t('pages.entityManagement.countSheetDrawer.minInstrumentsError');
  }
  return errors;
}

interface CountSheetDrawerProps {
  open: boolean;
  mode: DrawerMode;
  selectedCountSheet: CountSheetEntity | null;
  onClose: () => void;
  onSuccess?: () => void;
}

export const CountSheetDrawer = ({
  open,
  mode,
  selectedCountSheet,
  onClose,
  onSuccess,
}: CountSheetDrawerProps) => {
  const { t } = useTranslation(['periverse']);
  const entityName = t('pages.entityManagement.countSheetDrawer.entityName');

  const [trayId, setTrayId] = useState('');
  const [trayName, setTrayName] = useState('');
  const [selectedTray, setSelectedTray] = useState<AssetCatalogItemEntity | null>(null);
  const [traySearch, setTraySearch] = useState('');
  const [trayPopupOpen, setTrayPopupOpen] = useState(false);
  const debouncedTraySearch = useDebounce(traySearch);
  const [instruments, setInstruments] = useState<InstrumentFormItem[]>([]);
  const [instrumentSearch, setInstrumentSearch] = useState('');
  const debouncedInstrumentSearch = useDebounce(instrumentSearch);
  const [instrumentPopupOpen, setInstrumentPopupOpen] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [activeDetailsLocalId, setActiveDetailsLocalId] = useState<string | null>(null);

  const createMutation = useCreateCountSheet();
  const replaceMutation = useReplaceCountSheet();
  const isPending = createMutation.isPending || replaceMutation.isPending;

  const { data: existingCountSheets, isSuccess: countSheetsLoaded } = useCountSheets();
  const assignedTrayNames = new Set(
    (existingCountSheets ?? []).map((cs) => cs.trayName.toLowerCase())
  );

  const { data: traySearchData, isFetching: trayLoading } = useAssetCatalog(
    { type: 'TRAY', name: debouncedTraySearch, limit: 25 },
    { enabled: mode === DrawerMode.Create && open && countSheetsLoaded }
  );
  const trayResults = (traySearchData?.assetCatalogItems ?? []).filter(
    (r) => !assignedTrayNames.has(r.name.toLowerCase())
  );

  const {
    data: searchData,
    isFetching: instrumentsLoading,
    isPlaceholderData: instrumentsStale,
  } = useUntrackedAssetCatalog(
    { type: UntrackedAssetType.INSTRUMENT, search: debouncedInstrumentSearch || undefined, limit: 25 },
    { enabled: open }
  );
  const addedCatalogItemIds = new Set(instruments.map((i) => i.catalogItemId));
  const searchResults = instrumentsStale
    ? []
    : (searchData?.items ?? []).filter((r) => !addedCatalogItemIds.has(r.id));

  const resetCreateForm = () => {
    setTrayId('');
    setTrayName('');
    setSelectedTray(null);
    setTraySearch('');
    setTrayPopupOpen(false);
    setInstruments([]);
    setInstrumentSearch('');
    setInstrumentPopupOpen(false);
    setErrors({});
    setTouched(false);
  };

  useEffect(() => {
    if (!open) return;
    if (mode === DrawerMode.Edit && selectedCountSheet) {
      setTrayId(selectedCountSheet.trayId);
      setTrayName(selectedCountSheet.trayName);
      setTraySearch('');
      setInstruments(
        selectedCountSheet.instruments.map((inst, index) => ({
          localId: crypto.randomUUID(),
          catalogItemId: inst.catalogItemId,
          displayName: inst.displayName ?? '',
          expectedQuantity: inst.expectedQuantity,
          order: inst.order ?? index + 1,
          notes: inst.notes ?? '',
          group: inst.group ?? null,
          set: inst.set ?? null,
          instrumentTaxonomyClassId: inst.instrumentTaxonomyClassId ?? null,
          instrumentTaxonomyClassName: inst.instrumentTaxonomyClassName ?? null,
          instrumentTaxonomyCategoryName: inst.instrumentTaxonomyCategoryName ?? null,
          productNumber: inst.productNumber ?? null,
        }))
      );
    } else {
      resetCreateForm();
    }
    setInstrumentSearch('');
    setErrors({});
    setTouched(false);
  }, [open, mode, selectedCountSheet]);

  const handleReorder = (params: GridRowOrderChangeParams) => {
    setInstruments((prev) => {
      const result = [...prev];
      const [removed] = result.splice(params.oldIndex, 1);
      result.splice(params.targetIndex, 0, removed);
      return result.map((item, idx) => ({ ...item, order: idx + 1 }));
    });
  };

  const handleQuantityChange = (localId: string, delta: number) => {
    setInstruments((prev) =>
      prev.map((item) =>
        item.localId === localId
          ? { ...item, expectedQuantity: Math.max(1, item.expectedQuantity + delta) }
          : item
      )
    );
  };

  const handleDetailsClick = (localId: string) => {
    setActiveDetailsLocalId(localId);
    setDetailsDialogOpen(true);
  };

  const handleDetailsSave = ({ note, group, set }: InstrumentDetails) => {
    setInstruments((prev) =>
      prev.map((item) =>
        item.localId === activeDetailsLocalId
          ? { ...item, notes: note ?? '', group, set }
          : item
      )
    );
  };

  const handleDelete = (localId: string) => {
    setInstruments((prev) => prev.filter((item) => item.localId !== localId));
  };

  const handleSubmit = async () => {
    setTouched(true);
    const validationErrors = validate(trayName, trayId, mode, instruments, t);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});

    const instrumentDtos = instruments.map((item, index) => ({
      catalogItemId: item.catalogItemId,
      expectedQuantity: item.expectedQuantity,
      displayName: item.displayName || undefined,
      order: index + 1,
      notes: item.notes || undefined,
      group: item.group,
      set: item.set,
    }));

    try {
      if (mode === DrawerMode.Create) {
        await createMutation.mutateAsync({ trayId, trayName, instruments: instrumentDtos });
        onSuccess?.();
        resetCreateForm();
      } else if (mode === DrawerMode.Edit && selectedCountSheet) {
        await replaceMutation.mutateAsync({
          id: selectedCountSheet.id,
          dto: { trayName: trayName.trim(), instruments: instrumentDtos },
        });
        onSuccess?.();
        onClose();
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : t('pages.entityManagement.drawer.errorUnexpected');
      setErrors((prev) => ({ ...prev, apiError: message }));
    }
  };

  const activeDetailsItem = instruments.find((i) => i.localId === activeDetailsLocalId);

  return (
    <>
      <SideDrawer
        open={open}
        title={
          mode === DrawerMode.Create
            ? t('pages.entityManagement.drawer.titleCreate', { entityName })
            : t('pages.entityManagement.drawer.titleEdit', { entityName })
        }
        onClose={onClose}
        footer={
          <Button
            variant="contained"
            fullWidth
            size="large"
            disabled={isPending}
            onClick={() => void handleSubmit()}
            sx={{ borderRadius: '24px', height: 52 }}
          >
            {isPending
              ? t('pages.entityManagement.drawer.submitting')
              : t('pages.entityManagement.drawer.submit')}
          </Button>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {mode === DrawerMode.Create ? (
            <Autocomplete
              options={trayResults}
              getOptionLabel={(option) => option.name}
              filterOptions={(x) => x}
              open={trayPopupOpen && (trayResults.length > 0 || trayLoading)}
              onOpen={() => setTrayPopupOpen(true)}
              onClose={() => setTrayPopupOpen(false)}
              value={selectedTray}
              inputValue={traySearch}
              onInputChange={(_, value, reason) => {
                if (reason === 'input') {
                  setTraySearch(value);
                  setTrayId('');
                  setTrayName('');
                  setSelectedTray(null);
                  if (touched) setErrors(validate('', '', mode, instruments, t));
                }
              }}
              onChange={(_, tray) => {
                setSelectedTray(tray);
                setTrayPopupOpen(false);
                if (tray) {
                  setTrayId(tray.id);
                  setTrayName(tray.name);
                  setTraySearch(tray.name);
                  if (touched) setErrors(validate(tray.name, tray.id, mode, instruments, t));
                } else {
                  setTrayId('');
                  setTrayName('');
                  setTraySearch('');
                  if (touched) setErrors(validate('', '', mode, instruments, t));
                }
              }}
              loading={trayLoading}
              loadingText={t('pages.entityManagement.countSheetDrawer.trayLoadingText')}
              noOptionsText={null}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  size="small"
                  label={`${t('pages.entityManagement.countSheetDrawer.trayNameLabel')} *`}
                  error={touched && !!errors.trayName}
                  helperText={touched ? errors.trayName : undefined}
                  slotProps={{
                    formHelperText: { sx: { ml: '5px' } },
                    input: {
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              )}
            />
          ) : (
            <TextField
              fullWidth
              size="small"
              disabled
              label={t('pages.entityManagement.countSheetDrawer.trayNameLabel')}
              value={trayName}
            />
          )}

          <Box>
            <Typography variant="body2" display="block" sx={{ mb: 1 }}>
              {t('pages.entityManagement.countSheetDrawer.addInstrumentsLabel')} *
            </Typography>
            <Autocomplete
              options={searchResults}
              getOptionLabel={(option) => option?.name ?? ''}
              filterOptions={(x) => x}
              inputValue={instrumentSearch}
              onInputChange={(_, value, reason) => {
                if (reason === 'input') setInstrumentSearch(value);
              }}
              value={null}
              onChange={(_, result) => {
                if (!result) return;
                const updated = [
                  ...instruments,
                  {
                    localId: crypto.randomUUID(),
                    catalogItemId: result.id,
                    displayName: result.name,
                    expectedQuantity: 1,
                    order: instruments.length + 1,
                    notes: '',
                    group: null,
                    set: null,
                    instrumentTaxonomyClassId: result.instrumentTaxonomyClassId ?? null,
                    instrumentTaxonomyClassName: result.instrumentTaxonomyClassName ?? null,
                    instrumentTaxonomyCategoryName: result.instrumentTaxonomyCategoryName ?? null,
                    productNumber: result.productNumber ?? null,
                  },
                ];
                setInstruments(updated);
                if (touched) setErrors(validate(trayName, trayId, mode, updated, t));
              }}
              open={instrumentPopupOpen && !!(instrumentsLoading || searchResults.length > 0)}
              onOpen={() => setInstrumentPopupOpen(true)}
              onClose={() => setInstrumentPopupOpen(false)}
              loading={instrumentsLoading}
              loadingText={t('pages.entityManagement.countSheetDrawer.instrumentLoadingText')}
              noOptionsText={null}
              renderOption={(props, result) => {
                const { key, ...liProps } = props;
                return (
                  <Box component="li" key={key} {...liProps} sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 48 }}>
                    <InstrumentThumbnail taxonomyClassId={result.instrumentTaxonomyClassId} />
                    <Box>
                      {result.instrumentTaxonomyCategoryName && (
                        <Typography
                          variant="caption"
                          sx={{ color: 'text.secondary', display: 'block' }}
                        >
                          {result.instrumentTaxonomyCategoryName}
                        </Typography>
                      )}
                      <Typography variant="body2">{result.name}</Typography>
                      {result.productNumber && (
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                          {result.productNumber}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  size="small"
                  label={t('pages.entityManagement.countSheetDrawer.instrumentSearchPlaceholder')}
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <>
                          {instrumentSearch !== '' && (
                            <IconButton
                              aria-label={t(
                                'pages.entityManagement.countSheetDrawer.clearInstrumentSearchAriaLabel'
                              )}
                              size="small"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => {
                                setInstrumentSearch('');
                                setInstrumentPopupOpen(false);
                              }}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          )}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    },
                  }}
                />
              )}
            />
          </Box>

          {touched && errors.instruments && (
            <Alert severity="error">{errors.instruments}</Alert>
          )}

          <InstrumentListGrid
            rows={instruments}
            onReorder={handleReorder}
            onQuantityChange={handleQuantityChange}
            onNoteClick={handleDetailsClick}
            onDelete={handleDelete}
          />

          {touched && errors.apiError && <Alert severity="error">{errors.apiError}</Alert>}
        </Box>
      </SideDrawer>

      <InstrumentDetailsDialog
        open={detailsDialogOpen}
        initialNote={activeDetailsItem?.notes ?? null}
        initialGroup={activeDetailsItem?.group ?? null}
        initialSet={activeDetailsItem?.set ?? null}
        onSave={handleDetailsSave}
        onClose={() => setDetailsDialogOpen(false)}
      />
    </>
  );
};
